/**
 * AG Grid Cell Renderers for PACS Table
 * These renderers match the existing Ant Design table styling
 */

import React from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import { Tag, Tooltip, Button, Dropdown, Menu, Select, Popconfirm } from 'antd';
import { AlertOutlined } from '@ant-design/icons';
import { hasPermission } from '../../utils/helper';
import { Permissions } from '../../constants/fieldLabels';

/**
 * Status tag color mapping
 */
const getStatusStyle = (status: string) => {
  const baseStyle: React.CSSProperties = {
    height: '22px',
    borderRadius: '4px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '22px',
    letterSpacing: '0.06px',
    verticalAlign: 'middle',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px',
    minWidth: '72px',
    textAlign: 'center',
  };

  switch (status) {
    case 'SCANNED':
      return { ...baseStyle, background: '#DEF0FF', color: '#003FD2' };
    case 'SIGNEDOFF':
      return { ...baseStyle, background: '#DAF3EE', color: '#00795C' };
    case 'DRAFTED':
      return { ...baseStyle, background: '#FEECD5', color: '#AC5000' };
    default:
      return { ...baseStyle, background: '#DEF0FF', color: '#003FD2' };
  }
};

/**
 * Status Cell Renderer
 * Displays status as a colored tag
 */
export const StatusCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const status = params.data?.order_workflow?.ow_pacs_status || '';

  if (!status) return <span>-</span>;

  return (
    <Tag style={getStatusStyle(status)}>
      {status.replaceAll('_', ' ')}
    </Tag>
  );
};

/**
 * Modality Cell Renderer
 * Displays modality in a styled badge
 */
export const ModalityCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const modality = params.data?.pacs_order?.po_modality || '';

  if (!modality) return <span>-</span>;

  return (
    <span
      style={{
        background: '#F5F0FA',
        border: '1px solid #E0D8EA',
        height: '24px',
        borderRadius: '4px',
        padding: '2px 8px',
        display: 'inline-block',
        lineHeight: '20px',
        fontSize: '13px',
        fontWeight: 500,
        color: '#4a4a4a',
      }}
    >
      {modality}
    </span>
  );
};

/**
 * Patient Name Cell Renderer
 * Clickable button that opens report editor, with emergency indicator
 * Accesses callbacks from AG Grid context (passed via context prop)
 */
export const PatientNameCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const patientName = record?.pacs_order?.patient?.pat_name || '';
  const isEmergency = record?.pacs_order?.po_priority?.toUpperCase() === 'EMERGENCY';
  const currentlyBlocked = record?.order_workflow?.ow_reporting_blocked === true;
  const studyUID = record?.ps_study_uid;

  // Access callbacks from AG Grid context
  const onOpenReport = params.context?.onOpenReport;
  const onOpenRadiant = params.context?.onOpenRadiant;
  const enableRadiantViewer = params.context?.enableRadiantViewer;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentlyBlocked && onOpenReport) {
      onOpenReport(record);
    }
  };

  const handleRadiantClick = (e: any) => {
    e.domEvent?.stopPropagation();
    if (studyUID && onOpenRadiant) {
      onOpenRadiant(studyUID);
    }
  };

  const buttonContent = (
    <Button
      type="link"
      onClick={handleClick}
      style={{
        padding: 0,
        height: 'auto',
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
      }}
      disabled={currentlyBlocked}
    >
      <Tooltip title={patientName}>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
            maxWidth: isEmergency ? 'calc(100% - 22px)' : '100%',
            color: currentlyBlocked ? '#b0b0b0' : '#2563EB',
          }}
        >
          {patientName}
        </span>
      </Tooltip>
      {isEmergency && (
        <Tooltip title="Emergency Case">
          <AlertOutlined
            style={{
              color: '#FF4D4F',
              marginLeft: '6px',
              fontSize: '14px',
              animation: 'pulse 2s ease-in-out infinite',
            }}
          />
        </Tooltip>
      )}
    </Button>
  );

  // Add context menu for Radiant Viewer if enabled
  if (enableRadiantViewer && onOpenRadiant && studyUID) {
    return (
      <Dropdown
        overlay={
          <Menu>
            <Menu.Item key="radiant" onClick={handleRadiantClick}>
              Open in Radiant Viewer
            </Menu.Item>
          </Menu>
        }
        trigger={['contextMenu']}
      >
        <div style={{ width: '100%' }}>{buttonContent}</div>
      </Dropdown>
    );
  }

  return <div style={{ width: '100%' }}>{buttonContent}</div>;
};

/**
 * Tooltip Text Cell Renderer
 * Wraps text content with ellipsis and tooltip
 */
interface TooltipTextRendererProps extends ICellRendererParams {
  valueGetter?: (data: any) => string;
}

export const TooltipTextCellRenderer: React.FC<TooltipTextRendererProps> = (params) => {
  let value = params.value;

  // If custom valueGetter provided, use it
  if (params.valueGetter) {
    value = params.valueGetter(params.data);
  }

  if (!value) return <span style={{ color: '#b0b0b0', textAlign: 'center', width: '100%', display: 'block' }}>-</span>;

  return (
    <Tooltip title={value}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {value}
      </div>
    </Tooltip>
  );
};

/**
 * Patient ID Cell Renderer
 */
export const PatientIdCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const patId = params.data?.pacs_order?.patient?.pat_pin || '';

  if (!patId) return <span>-</span>;

  return (
    <Tooltip title={patId}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {patId}
      </div>
    </Tooltip>
  );
};

/**
 * Body Part Cell Renderer
 */
export const BodyPartCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const bodyPart = params.data?.pacs_order?.po_body_part || '';

  if (!bodyPart) return <span>-</span>;

  return (
    <Tooltip title={bodyPart}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {bodyPart}
      </div>
    </Tooltip>
  );
};

/**
 * Study Description Cell Renderer
 */
export const StudyDescCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const studyDesc = params.data?.pacs_order?.po_diag_desc || '';
  const priority = params.data?.pacs_order?.po_priority || '';

  if (!studyDesc) return <span>-</span>;

  const tooltipText = priority ? `${studyDesc} (Priority: ${priority})` : studyDesc;

  return (
    <Tooltip title={tooltipText}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {studyDesc}
      </div>
    </Tooltip>
  );
};

/**
 * Series/Image Count Cell Renderer
 */
export const SeriesCountCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const seriesCount = params.data?.ps_series_count || 0;
  const imageCount = params.data?.ps_image_count || 0;
  const isIngesting = !params.data?.is_study_ingestion_complete;

  const tooltipText = isIngesting
    ? 'Images are still being received from modality'
    : `Series: ${seriesCount}, Images: ${imageCount}`;

  return (
    <Tooltip title={tooltipText}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 6,
          width: '100%',
        }}
      >
        <span>
          {seriesCount} / {imageCount}
        </span>
      </div>
    </Tooltip>
  );
};

/**
 * Accession Number Cell Renderer
 */
export const AccessionNumberCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const accNo = params.data?.pacs_order?.po_acc_no || '';

  if (!accNo) return <span>-</span>;

  return (
    <Tooltip title={accNo}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {accNo}
      </div>
    </Tooltip>
  );
};

/**
 * Referring Doctor Cell Renderer
 */
export const RefDoctorCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const refDoc = params.data?.pacs_order?.po_ref_doc || '';

  return (
    <Tooltip title={refDoc || 'No referring doctor'}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          textAlign: refDoc ? 'left' : 'center',
          color: refDoc ? '#4a4a4a' : '#b0b0b0',
        }}
      >
        {refDoc || '-'}
      </div>
    </Tooltip>
  );
};

/**
 * Date/Time Cell Renderer
 */
import moment from 'moment';

export const DateTimeCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const value = params.value;

  if (!value) return <span>-</span>;

  const formatted = moment(value).utcOffset('+05:30').format('Do MMM YYYY, hh:mmA');

  return <span>{formatted}</span>;
};

/**
 * Reported By Cell Renderer
 */
export const ReportedByCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const userName = params.data?.reportedBy || '';

  if (!userName) return <span>-</span>;

  return (
    <Tooltip title={userName}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {userName}
      </div>
    </Tooltip>
  );
};

/**
 * Signed By Cell Renderer
 */
export const SignedByCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const userName = params.data?.signedBy || '';

  if (!userName) return <span>-</span>;

  return (
    <Tooltip title={userName}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {userName}
      </div>
    </Tooltip>
  );
};

/**
 * Assigned Radiologist Cell Renderer
 * Displays a dropdown for users with ASSIGN_CASES permission, plain text otherwise
 * Accesses userList and onRadiologistAssignment from AG Grid context
 */
export const AssignedRadiologistCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const assignedRadId = record?.order_workflow?.ow_assigned_rad_id;
  // const assignedRadName = record?.order_workflow?.ow_assigned_rad_name;
  const canAssign = hasPermission(Permissions.ASSIGN_CASES);

  // Access data from AG Grid context
  const userList = params.context?.userList || [];
  const onRadiologistAssignment = params.context?.onRadiologistAssignment;
  const pacs_status = record?.order_workflow?.ow_pacs_status;
  const isSignedOff = pacs_status === 'SIGNEDOFF';
  if (canAssign) {
    // Filter to only radiologists and map to Select options
    const radiologistOptions = userList
      ?.filter((u: any) => u.user_type === 'radiologist')
      .map((user: any) => ({
        label: user.user_fullname,
        value: user.username,
      })) || [];

  // For SIGNEDOFF status, show Select disabled
      return (
        <Select
          value={assignedRadId || undefined}
          placeholder="Unassigned"
          allowClear={!isSignedOff}
          disabled={isSignedOff}
          showSearch={!isSignedOff}
          options={radiologistOptions}
          optionFilterProp="label"
          onChange={(value) => {
            if (onRadiologistAssignment) {
              onRadiologistAssignment(record, value, params.rowIndex);
            }
          }}
          style={{
            width: '100%',
            minWidth: '150px',
          }}
        />
      );
  }

  // Show plain text for users without permission
  return (
    <Tooltip title={assignedRadId || 'Unassigned'}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          color: assignedRadId ? '#4a4a4a' : '#b0b0b0',
        }}
      >
        {assignedRadId || '-'}
      </div>
    </Tooltip>
  );
};

/**
 * Assigned Technician Cell Renderer
 * Shows assigned technician name or "Self Assign" button with Popconfirm
 * Only visible to technician users (controlled by column visibility)
 * Accesses onAssignToSelfTechnician from AG Grid context
 */
export const AssignedTechnicianCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const assignedTechnician = record?.order_workflow?.ow_assigned_technician;

  // Access callback from AG Grid context
  const onAssignToSelfTechnician = params.context?.onAssignToSelfTechnician;

  // If already assigned, show the technician name
  if (assignedTechnician) {
    return (
      <Tooltip title={assignedTechnician}>
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {assignedTechnician}
        </div>
      </Tooltip>
    );
  }

  // Show "Self Assign" button with confirmation
  return (
    <Popconfirm
      title="Assign to yourself?"
      onConfirm={() => {
        if (onAssignToSelfTechnician) {
          onAssignToSelfTechnician(record, params.rowIndex);
        }
      }}
      okText="Yes"
      cancelText="No"
      onCancel={(e) => e?.stopPropagation()}
    >
      <Button
        size="small"
        onClick={(e) => e.stopPropagation()}
      >
        Self Assign
      </Button>
    </Popconfirm>
  );
};
